/**
 * useFCMToken Hook
 *
 * Manages Firebase Cloud Messaging (FCM) tokens for push notifications.
 *
 * Two-step process:
 * 1. requestBrowserPermission() - Asks the browser for notification permission
 * 2. registerFCMToken() - Registers the FCM token with Firebase (call after permission granted)
 *
 * Token Storage Format (v2 with metadata):
 * {
 *   fcmTokens: [
 *     { token: "abc123...", registeredAt: "2024-01-15T...", userAgent: "Mozilla/5.0..." },
 *     { token: "def456...", registeredAt: "2024-01-16T...", userAgent: "Mozilla/5.0..." }
 *   ]
 * }
 *
 * Features:
 * - Auto-refresh on app load (call registerFCMToken when user has notifications enabled)
 * - Max 5 tokens per user (removes oldest when limit exceeded)
 * - Backwards compatible with old format (array of plain strings)
 *
 * Browser Compatibility Notes:
 * - Chrome/Firefox: Standard behavior, permission prompt appears immediately
 * - Edge: May hang if Windows notifications are disabled at OS level.
 *   We add a timeout and service worker pre-registration to handle this.
 *
 * Usage:
 *   const { permissionStatus, requestBrowserPermission, registerFCMToken, isRequestingPermission } = useFCMToken();
 *
 *   // Step 1: Request browser permission
 *   const granted = await requestBrowserPermission();
 *
 *   // Step 2: If granted, register FCM token
 *   if (granted) {
 *     await registerFCMToken();
 *   }
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { getToken } from 'firebase/messaging';
import { doc, runTransaction } from 'firebase/firestore';
import { useFirebaseStore } from '../store/firebaseStore';
import { logger } from '../utils/logger';
import { getOrCreateDeviceId, getCurrentDeviceId } from '../utils/deviceId';

// Maximum number of FCM tokens to keep per user
const MAX_TOKENS_PER_USER = 5;

/** Token with metadata for storage */
export interface FCMTokenEntry {
  deviceId: string;        // Stable UUID stored in localStorage - KEY FIELD for device operations
  token: string;
  registeredAt: string;
  userAgent: string;
  enabled: boolean;        // Per-device push toggle (default: true)
  deviceName?: string;     // User-friendly name (auto-generated from userAgent)
}

export type PermissionStatus = NotificationPermission | 'unsupported' | 'timeout';

export interface UseFCMTokenReturn {
  /** Current browser notification permission status */
  permissionStatus: PermissionStatus;
  /** Request browser notification permission only. Returns true if granted. */
  requestBrowserPermission: () => Promise<boolean>;
  /** Register FCM token after permission is granted. Returns true if successful. */
  registerFCMToken: () => Promise<boolean>;
  /** Whether a browser permission request is in progress */
  isRequestingPermission: boolean;
  /** Whether FCM token registration is in progress */
  isRegisteringToken: boolean;
  /** Refresh the permission status from the browser */
  refreshPermissionStatus: () => void;
  /** Update the enabled status for a device by deviceId */
  updateDeviceEnabled: (deviceId: string, enabled: boolean) => Promise<boolean>;
  /** Remove a device by deviceId */
  removeDevice: (deviceId: string) => Promise<boolean>;
  /** The current device's ID (for "This device" badge) */
  currentDeviceId: string | null;
}

// Timeout for permission request (15 seconds)
// Edge can hang indefinitely if Windows notifications are disabled
const PERMISSION_TIMEOUT_MS = 15000;

/**
 * Generate a user-friendly device name from a userAgent string.
 * Returns something like "Chrome on Windows" or "Safari on iOS".
 */
function generateDeviceName(userAgent: string): string {
  if (!userAgent) return 'Unknown Device';

  // Detect browser (order matters - Edge includes "Chrome" in UA)
  let browser = 'Browser';
  if (userAgent.includes('Edg/')) browser = 'Edge';
  else if (userAgent.includes('Firefox/')) browser = 'Firefox';
  else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) browser = 'Safari';
  else if (userAgent.includes('Chrome/')) browser = 'Chrome';

  // Detect OS
  let os = '';
  if (userAgent.includes('Windows')) os = 'Windows';
  else if (userAgent.includes('Mac OS')) os = 'macOS';
  else if (userAgent.includes('Linux') && !userAgent.includes('Android')) os = 'Linux';
  else if (userAgent.includes('Android')) os = 'Android';
  else if (userAgent.includes('iPhone')) os = 'iPhone';
  else if (userAgent.includes('iPad')) os = 'iPad';

  return os ? `${browser} on ${os}` : browser;
}

/**
 * Wrapper for Notification.requestPermission() with timeout handling.
 * Edge can hang indefinitely if notifications are blocked at the OS level.
 */
const requestPermissionWithTimeout = async (): Promise<NotificationPermission | 'timeout'> => {
  return new Promise((resolve) => {
    let resolved = false;

    // Set up timeout
    const timeoutId = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        logger.warn('[FCM] Permission request timed out - likely blocked at OS level');
        resolve('timeout');
      }
    }, PERMISSION_TIMEOUT_MS);

    // Request permission
    Notification.requestPermission()
      .then((result) => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeoutId);
          resolve(result);
        }
      })
      .catch((error) => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeoutId);
          logger.error('[FCM] Permission request error:', error);
          resolve('denied');
        }
      });
  });
};

/**
 * Ensures the Firebase messaging service worker is registered.
 * This is required before requesting FCM tokens and helps with Edge compatibility.
 * IMPORTANT: SW must be served from root (/) for scope to cover manifest's start_url for PWA install.
 */
const ensureServiceWorkerRegistered = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) {
    logger.warn('[FCM] Service workers not supported');
    return null;
  }

  try {
    // Check if already registered at root scope
    const existingReg = await navigator.serviceWorker.getRegistration('/');
    if (existingReg) {
      logger.log('[FCM] Service worker already registered');
      return existingReg;
    }

    // Register the service worker from root for correct scope
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    logger.log('[FCM] Service worker registered:', registration.scope);
    return registration;
  } catch (error) {
    logger.error('[FCM] Service worker registration failed:', error);
    return null;
  }
};

export const useFCMToken = (): UseFCMTokenReturn => {
  const { messaging, db, currentUser, setCurrentUser } = useFirebaseStore();

  // Initialize with current browser permission status
  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;
  });

  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const [isRegisteringToken, setIsRegisteringToken] = useState(false);

  // Bug 4: Ref-based lock to prevent double-click race conditions
  const isRequestingRef = useRef(false);
  const isRegisteringRef = useRef(false);

  // Keep permission status in sync with browser state
  useEffect(() => {
    if (typeof Notification === 'undefined') {
      setPermissionStatus('unsupported');
      return;
    }
    setPermissionStatus(Notification.permission);
  }, []);

  /**
   * Manually refresh the permission status from the browser.
   * Useful after the user may have changed settings externally.
   */
  const refreshPermissionStatus = useCallback(() => {
    if (typeof Notification === 'undefined') {
      setPermissionStatus('unsupported');
    } else {
      setPermissionStatus(Notification.permission);
    }
  }, []);

  /**
   * Request browser notification permission only.
   * This is step 1 of the two-step process.
   *
   * @returns true if permission was granted
   */
  const requestBrowserPermission = useCallback(async (): Promise<boolean> => {
    if (typeof Notification === 'undefined') {
      logger.warn('[FCM] Notifications not supported in this browser');
      setPermissionStatus('unsupported');
      return false;
    }

    // If already granted, just return true
    if (Notification.permission === 'granted') {
      setPermissionStatus('granted');
      return true;
    }

    // Bug 4: Prevent double-click race condition with ref-based lock
    if (isRequestingRef.current) {
      logger.log('[FCM] Permission request already in progress, skipping');
      return false;
    }
    isRequestingRef.current = true;
    setIsRequestingPermission(true);

    try {
      // Bug 7: Removed redundant SW registration - registerFCMToken handles this
      // (calling it here was redundant since registerFCMToken also calls it)

      // Request Permission with timeout (Edge can hang indefinitely)
      const permission = await requestPermissionWithTimeout();
      setPermissionStatus(permission);

      if (permission === 'timeout') {
        logger.warn('[FCM] Permission request timed out');
        return false;
      }

      return permission === 'granted';
    } catch (err) {
      logger.error('[FCM] Error requesting browser permission:', err);
      return false;
    } finally {
      isRequestingRef.current = false;
      setIsRequestingPermission(false);
    }
  }, []);

  /**
   * Register FCM token with Firebase and save to Firestore.
   * This is step 2 of the two-step process.
   * Should only be called after browser permission is granted.
   *
   * Features:
   * - Uses Firestore transaction to prevent race conditions (Bug 1 & 6)
   * - Stores token with metadata (registeredAt, userAgent)
   * - Enforces max 5 tokens per user (removes oldest when exceeded)
   * - Backwards compatible with old format (migrates on write)
   * - Safe to call on every app load (only writes if token is new)
   * - Updates Zustand store after successful write to prevent stale cache
   *
   * @returns true if token was successfully registered
   */
  const registerFCMToken = useCallback(async (): Promise<boolean> => {
    // Check prerequisites
    if (!messaging) {
      logger.warn('[FCM] Messaging not initialized');
      return false;
    }
    if (!db || !currentUser) {
      logger.warn('[FCM] No database or user context');
      return false;
    }
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      logger.warn('[FCM] Browser permission not granted');
      return false;
    }

    // Bug 4: Prevent double-click race condition with ref-based lock
    if (isRegisteringRef.current) {
      logger.log('[FCM] Token registration already in progress, skipping');
      return false;
    }
    isRegisteringRef.current = true;
    setIsRegisteringToken(true);

    try {
      // Ensure service worker is registered
      const swRegistration = await ensureServiceWorkerRegistered();

      // Get FCM Token
      const tokenOptions: { vapidKey: string; serviceWorkerRegistration?: ServiceWorkerRegistration } = {
        vapidKey: 'BBDhVt36f_C4_dRxvjtHg-ungGEFyfNpiusezbTJi27GsAvxK5lqRx1ltPdicxrvv1c0yrmQYGzc5h1OL4ANi_I',
      };

      if (swRegistration) {
        tokenOptions.serviceWorkerRegistration = swRegistration;
      }

      const currentToken = await getToken(messaging, tokenOptions);

      if (!currentToken) {
        logger.warn('[FCM] No registration token available');
        return false;
      }

      const userRef = doc(db, 'users', currentUser.email);

      // Get or create a stable device ID for this browser/profile
      const deviceId = getOrCreateDeviceId();
      const currentUserAgent = navigator.userAgent;
      const deviceName = generateDeviceName(currentUserAgent);

      // Bug 1 & 6: Use Firestore transaction for atomic read-modify-write
      // This prevents race conditions when multiple tabs/devices register simultaneously
      const finalTokens = await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef);

        // Get existing tokens from Firestore (source of truth during transaction)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const existingTokens: (string | any)[] = userDoc.data()?.fcmTokens || [];

        // Normalize existing tokens to new format (handle migration from old formats)
        const normalizedTokens: FCMTokenEntry[] = existingTokens.map((entry) => {
          if (typeof entry === 'string') {
            // Old format: plain string - migrate to new format with unknown metadata
            return {
              deviceId: crypto.randomUUID(), // Generate new deviceId for legacy tokens
              token: entry,
              registeredAt: '1970-01-01T00:00:00.000Z', // Mark as old so it gets cleaned up first
              userAgent: 'unknown (migrated)',
              enabled: true,
              deviceName: 'Unknown Device (migrated)',
            };
          }
          // Handle entries without deviceId (pre-v3 format)
          if (!entry.deviceId) {
            return {
              ...entry,
              deviceId: crypto.randomUUID(), // Generate new deviceId
              enabled: entry.enabled ?? true, // Default to enabled
              deviceName: entry.deviceName || generateDeviceName(entry.userAgent || ''),
            };
          }
          // Already has deviceId, ensure enabled field exists
          return {
            ...entry,
            enabled: entry.enabled ?? true,
            deviceName: entry.deviceName || generateDeviceName(entry.userAgent || ''),
          };
        });

        // Match by deviceId (stable across token refreshes)
        const existingDeviceIndex = normalizedTokens.findIndex((entry) => entry.deviceId === deviceId);

        if (existingDeviceIndex !== -1) {
          // Same device - update token but preserve enabled and deviceName settings
          const existingEntry = normalizedTokens[existingDeviceIndex];
          normalizedTokens[existingDeviceIndex] = {
            ...existingEntry,
            token: currentToken,
            registeredAt: new Date().toISOString(),
            userAgent: currentUserAgent,
            // Preserve user settings across token refreshes
            enabled: existingEntry.enabled,
            deviceName: existingEntry.deviceName || deviceName,
          };
          logger.firestore('Write', `users/${currentUser.email} (FCM token refresh for device ${deviceId.slice(0, 8)})`);
        } else {
          // New device - add new token entry
          const newEntry: FCMTokenEntry = {
            deviceId,
            token: currentToken,
            registeredAt: new Date().toISOString(),
            userAgent: currentUserAgent,
            enabled: true, // New devices default to enabled
            deviceName,
          };
          normalizedTokens.push(newEntry);
          logger.firestore('Write', `users/${currentUser.email} (FCM new device ${deviceId.slice(0, 8)})`);
        }

        // Clean up any duplicate tokens (same token on different deviceIds - shouldn't happen but safety)
        let finalTokenList = normalizedTokens.filter((entry, index) => {
          // Keep entries with unique tokens, preferring the one matching current deviceId
          const firstIndex = normalizedTokens.findIndex((e) => e.token === entry.token);
          if (firstIndex !== index) {
            // Duplicate token found - keep the one with matching deviceId, or the first one
            if (entry.deviceId === deviceId) return true;
            if (normalizedTokens[firstIndex].deviceId === deviceId) return false;
            return firstIndex === index;
          }
          return true;
        });

        // Enforce max tokens limit - remove oldest if exceeded
        if (finalTokenList.length > MAX_TOKENS_PER_USER) {
          // Sort by registeredAt (oldest first) and remove extras
          finalTokenList.sort((a, b) =>
            new Date(a.registeredAt).getTime() - new Date(b.registeredAt).getTime()
          );
          const removed = finalTokenList.splice(0, finalTokenList.length - MAX_TOKENS_PER_USER);
          logger.log(`[FCM] Removed ${removed.length} old token(s) to maintain limit of ${MAX_TOKENS_PER_USER}`);
        }

        // Write the updated tokens atomically
        transaction.update(userRef, {
          fcmTokens: finalTokenList,
          lastTokenUpdate: new Date().toISOString(),
        });

        return finalTokenList;
      });

      // Bug 6: Update Zustand store after successful transaction to prevent stale cache
      // This ensures the local cache reflects the Firestore state
      if (currentUser && setCurrentUser) {
        setCurrentUser({
          ...currentUser,
          fcmTokens: finalTokens,
        });
        logger.log('[FCM] Zustand store updated with new token list');
      }

      logger.log('[FCM] Token saved for user:', currentUser.email);
      return true;
    } catch (err) {
      logger.error('[FCM] Error registering FCM token:', err);
      return false;
    } finally {
      isRegisteringRef.current = false;
      setIsRegisteringToken(false);
    }
  }, [messaging, db, currentUser, setCurrentUser]);

  /**
   * Update the enabled status for a device by deviceId.
   * Uses a Firestore transaction for atomic updates.
   *
   * @param deviceId - The device ID to update
   * @param enabled - Whether push notifications should be enabled for this device
   * @returns true if update was successful
   */
  const updateDeviceEnabled = useCallback(async (deviceId: string, enabled: boolean): Promise<boolean> => {
    if (!db || !currentUser) {
      logger.warn('[FCM] No database or user context for updateDeviceEnabled');
      return false;
    }

    try {
      const userRef = doc(db, 'users', currentUser.email);

      const finalTokens = await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        const existingTokens: FCMTokenEntry[] = userDoc.data()?.fcmTokens || [];

        // Find and update the device by deviceId
        const updatedTokens = existingTokens.map((entry) => {
          if (entry.deviceId === deviceId) {
            return { ...entry, enabled };
          }
          return entry;
        });

        transaction.update(userRef, {
          fcmTokens: updatedTokens,
          lastTokenUpdate: new Date().toISOString(),
        });

        logger.firestore('Write', `users/${currentUser.email} (FCM device ${deviceId.slice(0, 8)} ${enabled ? 'enabled' : 'disabled'})`);
        return updatedTokens;
      });

      // Update Zustand store
      if (setCurrentUser) {
        setCurrentUser({
          ...currentUser,
          fcmTokens: finalTokens,
        });
      }

      logger.log(`[FCM] Device ${deviceId.slice(0, 8)} ${enabled ? 'enabled' : 'disabled'}`);
      return true;
    } catch (err) {
      logger.error('[FCM] Error updating device enabled status:', err);
      return false;
    }
  }, [db, currentUser, setCurrentUser]);

  /**
   * Remove a device by deviceId.
   * Uses a Firestore transaction for atomic updates.
   *
   * @param deviceId - The device ID to remove
   * @returns true if removal was successful
   */
  const removeDevice = useCallback(async (deviceId: string): Promise<boolean> => {
    if (!db || !currentUser) {
      logger.warn('[FCM] No database or user context for removeDevice');
      return false;
    }

    try {
      const userRef = doc(db, 'users', currentUser.email);

      const finalTokens = await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        const existingTokens: FCMTokenEntry[] = userDoc.data()?.fcmTokens || [];

        // Filter out the device by deviceId
        const updatedTokens = existingTokens.filter((entry) => entry.deviceId !== deviceId);

        transaction.update(userRef, {
          fcmTokens: updatedTokens,
          lastTokenUpdate: new Date().toISOString(),
        });

        logger.firestore('Write', `users/${currentUser.email} (FCM device ${deviceId.slice(0, 8)} removed)`);
        return updatedTokens;
      });

      // Update Zustand store
      if (setCurrentUser) {
        setCurrentUser({
          ...currentUser,
          fcmTokens: finalTokens,
        });
      }

      logger.log(`[FCM] Device ${deviceId.slice(0, 8)} removed`);
      return true;
    } catch (err) {
      logger.error('[FCM] Error removing device:', err);
      return false;
    }
  }, [db, currentUser, setCurrentUser]);

  // Get current device ID for "This device" badge
  const currentDeviceId = getCurrentDeviceId();

  return {
    permissionStatus,
    requestBrowserPermission,
    registerFCMToken,
    isRequestingPermission,
    isRegisteringToken,
    refreshPermissionStatus,
    updateDeviceEnabled,
    removeDevice,
    currentDeviceId,
  };
};
